import type { MediaType } from '@disy/lib/media-type';

import type { ActionOptions } from 'cadenza/action';
import { Action } from 'cadenza/action';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';

import i18n from './abstract-export-action.properties';

export type ExportActionOptions = ActionOptions & { dialogTitle?: string };

export class AbstractExportActionWithCanExportChecked extends Action {
  #mediaType;
  #dialogTitle;

  /**
   * @param mediaType - Media type of the export
   * @param options - Options
   * @param options.dialogTitle - Alternative title of the file export progress dialog
   */
  constructor(mediaType: MediaType, { dialogTitle = i18n('dialog.title.default'), ...options }: ExportActionOptions) {
    super(options);
    this.#mediaType = mediaType;
    this.#dialogTitle = dialogTitle;
  }

  get mediaType() {
    return this.#mediaType;
  }

  override async execute() {
    const dialog = await this._createFileExportProgressDialog();
    dialog.connectAndShow();
    return dialog.startExportAndShowProgress();
  }

  async _createFileExportProgressDialog() {
    const { FileExportWithCanExportProgressDialog } =
      await import('cadenza/file-export-with-can-export-progress-dialog/file-export-with-can-export-progress-dialog');
    return new FileExportWithCanExportProgressDialog({
      title: this.#dialogTitle,
      mediaType: this.mediaType,
      canExport: () => this.canExport(),
      startExport: () => this.startExport(),
    });
  }

  /**
   * Checks if the export is possible.
   * Must be overridden by subclass.
   */
  protected canExport(): Promise<WorkOrderId> {
    return Promise.reject(new Error('Not implemented by subclass'));
  }

  /**
   * Start the actual export.
   * Must be overridden by subclass.
   */
  protected startExport(): Promise<WorkOrderId> {
    return Promise.reject(new Error('Not implemented by subclass'));
  }
}
